import React, { useEffect } from 'react';
import { ArrowLeft, Target, Zap, Shield, Lock, Wifi, Smartphone, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const PenetrationTestingPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="dark-container">
      <Header />
      
      {/* Hero Section */}
      <section className="service-hero">
        <div className="dark-content-container">
          <Link to="/" className="back-link">
            <ArrowLeft size={20} />
            Back to Home
          </Link>
          
          <div className="service-hero-content">
            <div className="service-badge">
              <Target className="service-badge-icon" />
              <span>Advanced Penetration Testing</span>
            </div>
            
            <h1 className="service-title">
              Ethical Hacking & 
              <span className="service-title-accent"> Advanced Security Testing</span>
            </h1>
            
            <p className="service-description">
              Comprehensive penetration testing services that simulate real-world attacks to identify 
              vulnerabilities before malicious actors do. Our certified ethical hackers use the latest 
              techniques to test your defenses thoroughly.
            </p>
            
            <div className="service-stats">
              <div className="service-stat">
                <span className="service-stat-number">98%</span>
                <span className="service-stat-label">Vulnerability Discovery Rate</span>
              </div>
              <div className="service-stat">
                <span className="service-stat-number">15+</span>
                <span className="service-stat-label">Years Average Team Experience</span>
              </div>
              <div className="service-stat">
                <span className="service-stat-number">Zero</span>
                <span className="service-stat-label">False Positives Guarantee</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testing Types Section */}
      <section className="testing-types-section">
        <div className="dark-content-container">
          <h2 className="display-large">Comprehensive Testing Services</h2>
          
          <div className="testing-grid">
            <div className="testing-card">
              <Globe className="testing-icon" />
              <h3 className="heading-2">External Network Testing</h3>
              <p className="body-medium">
                Simulate attacks from external threat actors targeting your internet-facing assets, 
                including web applications, network services, and cloud infrastructure.
              </p>
              <ul className="testing-features">
                <li>Internet-facing asset discovery</li>
                <li>Web application security testing</li>
                <li>Network service exploitation</li>
                <li>Social engineering assessments</li>
                <li>Physical security testing</li>
              </ul>
            </div>

            <div className="testing-card">
              <Shield className="testing-icon" />
              <h3 className="heading-2">Internal Network Testing</h3>
              <p className="body-medium">
                Assess your internal network security by simulating insider threats and lateral 
                movement scenarios to test internal controls and segmentation.
              </p>
              <ul className="testing-features">
                <li>Lateral movement simulation</li>
                <li>Privilege escalation testing</li>
                <li>Active Directory attacks</li>
                <li>Network segmentation validation</li>
                <li>Data exfiltration scenarios</li>
              </ul>
            </div>

            <div className="testing-card">
              <Smartphone className="testing-icon" />
              <h3 className="heading-2">Mobile Application Testing</h3>
              <p className="body-medium">
                Comprehensive security assessment of iOS and Android applications, including 
                static analysis, dynamic testing, and runtime manipulation.
              </p>
              <ul className="testing-features">
                <li>OWASP Mobile Top 10 testing</li>
                <li>Binary analysis and reverse engineering</li>
                <li>API security assessment</li>
                <li>Device-specific vulnerability testing</li>
                <li>Data storage security validation</li>
              </ul>
            </div>

            <div className="testing-card">
              <Wifi className="testing-icon" />
              <h3 className="heading-2">Wireless Security Testing</h3>
              <p className="body-medium">
                Evaluate wireless network security including Wi-Fi, Bluetooth, and other 
                wireless protocols for configuration weaknesses and attack vectors.
              </p>
              <ul className="testing-features">
                <li>Wi-Fi security assessment</li>
                <li>Wireless protocol analysis</li>
                <li>Rogue access point detection</li>
                <li>Bluetooth security testing</li>
                <li>IoT device security validation</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Methodology Section */}
      <section className="methodology-section">
        <div className="dark-content-container">
          <h2 className="display-large">PTES-Based Testing Methodology</h2>
          
          <div className="methodology-grid">
            <div className="methodology-step">
              <div className="step-number">01</div>
              <div className="step-content">
                <h3 className="heading-2">Pre-Engagement & Scoping</h3>
                <p className="body-medium">
                  Detailed planning phase including scope definition, rules of engagement, 
                  and legal authorization to ensure safe and effective testing.
                </p>
                <ul className="step-features">
                  <li>Scope and objectives definition</li>
                  <li>Rules of engagement agreement</li>
                  <li>Emergency contact procedures</li>
                  <li>Testing timeline coordination</li>
                </ul>
              </div>
            </div>

            <div className="methodology-step">
              <div className="step-number">02</div>
              <div className="step-content">
                <h3 className="heading-2">Intelligence Gathering</h3>
                <p className="body-medium">
                  Comprehensive reconnaissance using OSINT and active scanning to map 
                  your attack surface and identify potential entry points.
                </p>
                <ul className="step-features">
                  <li>OSINT and passive reconnaissance</li>
                  <li>Active network discovery</li>
                  <li>Service enumeration</li>
                  <li>Technology fingerprinting</li>
                </ul>
              </div>
            </div>

            <div className="methodology-step">
              <div className="step-number">03</div>
              <div className="step-content">
                <h3 className="heading-2">Vulnerability Analysis</h3>
                <p className="body-medium">
                  Detailed vulnerability assessment combining automated scanning with 
                  manual testing to identify exploitable security weaknesses.
                </p>
                <ul className="step-features">
                  <li>Automated vulnerability scanning</li>
                  <li>Manual verification and testing</li>
                  <li>Custom exploit development</li>
                  <li>Risk impact assessment</li>
                </ul>
              </div>
            </div>

            <div className="methodology-step">
              <div className="step-number">04</div>
              <div className="step-content">
                <h3 className="heading-2">Exploitation & Post-Exploitation</h3>
                <p className="body-medium">
                  Controlled exploitation of vulnerabilities followed by post-exploitation 
                  activities to demonstrate real-world attack impact and business risk.
                </p>
                <ul className="step-features">
                  <li>Safe exploitation techniques</li>
                  <li>Privilege escalation testing</li>
                  <li>Lateral movement simulation</li>
                  <li>Data access validation</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Deliverables Section */}
      <section className="deliverables-section">
        <div className="dark-content-container">
          <h2 className="display-large">Comprehensive Reporting & Remediation</h2>
          
          <div className="deliverables-grid">
            <div className="deliverable-content">
              <h3 className="heading-2">Actionable Security Reports</h3>
              <p className="body-large">
                Detailed technical and executive reports with clear remediation guidance, 
                risk ratings, and proof-of-concept demonstrations for all findings.
              </p>
              
              <div className="deliverable-features">
                <div className="deliverable-feature">
                  <Lock className="feature-icon" />
                  <div>
                    <h4 className="heading-3">Executive Summary</h4>
                    <p className="body-small">Business risk assessment with strategic recommendations for leadership</p>
                  </div>
                </div>
                
                <div className="deliverable-feature">
                  <Target className="feature-icon" />
                  <div>
                    <h4 className="heading-3">Technical Findings</h4>
                    <p className="body-small">Detailed vulnerability descriptions with exploitation evidence and remediation steps</p>
                  </div>
                </div>
                
                <div className="deliverable-feature">
                  <Zap className="feature-icon" />
                  <div>
                    <h4 className="heading-3">Remediation Roadmap</h4>
                    <p className="body-small">Prioritized action plan with timelines and resource requirements</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="deliverable-stats">
              <div className="stat-item">
                <span className="stat-number">24-48hrs</span>
                <span className="stat-label">Critical Finding Notification</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">100%</span>
                <span className="stat-label">Findings Validation</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">90 Days</span>
                <span className="stat-label">Free Retest Period</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PenetrationTestingPage;